import { Link } from 'react-router';
import type { TargetDTO } from '@devrobot/shared';
import { LoaderCircle, Lock, TriangleAlert } from 'lucide-react';
import { StateDimensions } from '../../components/StateDimensions';
import { StatusDot } from '../../components/StatusBadge';
import { VisitButton } from '../../components/VisitButton';

/** Compact deployment-target summary used on project and VPS pages. */
export function TargetCard({ t }: { t: TargetDTO }) {
  return (
    <article className="card card-sm dr-surface dr-lift">
      <div className="card-body gap-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="card-title text-base">
              <Link to={`/targets/${t.id}`} className="link link-hover">
                {t.environment}
              </Link>
              {t.environmentProtected ? (
                <span className="badge badge-warning badge-soft badge-xs gap-1">
                  <Lock className="size-3" aria-hidden="true" />
                  protected
                </span>
              ) : null}
            </h3>
            <p className="text-sm text-base-content/65">
              on{' '}
              <Link className="link link-hover" to={`/vps/${t.vps.id}`}>
                {t.vps.name}
              </Link>{' '}
              · <StatusDot kind="connection" value={t.vps.connection} className="text-xs" />
            </p>
            <p className="font-mono text-xs text-base-content/55">{t.stack}</p>
          </div>
          <div className="flex flex-wrap gap-1">
            {t.config.pending ? <span className="badge badge-soft badge-warning badge-sm gap-1">
                <TriangleAlert className="size-3" aria-hidden="true" />
                configuration pending
              </span> : null}
            {t.activeJob ? (
              <Link to={`/jobs/${t.activeJob.id}`} className="badge badge-soft badge-info badge-sm gap-1 hover:brightness-95">
                <LoaderCircle className="size-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                {t.activeJob.kind} {t.activeJob.state}
              </Link>
            ) : null}
            {t.removedAt ? <span className="badge badge-soft badge-neutral badge-sm">removed</span> : null}
          </div>
        </div>
        <StateDimensions state={t.state} endpoints={t.endpoints} compact />
        {t.endpoints.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <VisitButton target={t} size="xs" />
            <span className="text-xs text-base-content/55">
              {t.endpoints.length === 1 ? t.endpoints[0].url : `${t.endpoints.length} public hosts`}
            </span>
          </div>
        ) : null}
        <div className="border-t border-hairline pt-3 text-xs text-base-content/65">
          {t.currentRelease ? (
            <span>
              Running <span className="font-mono">{t.currentRelease.branch}@{t.currentRelease.shortSha}</span>
              {t.currentRelease.commitTitle ? ` — ${t.currentRelease.commitTitle}` : ''}
            </span>
          ) : (
            'No release deployed'
          )}
        </div>
      </div>
    </article>
  );
}
